import React, { useState, useMemo } from "react";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    Legend,
    Label
} from "recharts";

import { MaxChartSize, VolunteerGroupableField } from "../../data/types";
import { CHART_COLORS, CHART_WIDTH_CLASSES } from "../../data";
import { VolunteerViewModal } from "./VolunteerViewModal";

type categoryType = { field: string, value: string }

type VolunteerPieChartProps = {
    title: string;
    data: { name: string; count: number }[];
    groupBy: VolunteerGroupableField;
    width?: MaxChartSize;
    onChartClick?: (category: categoryType) => void;
};

const CustomCenterLabel = (props: any) => {
    const { viewBox, totalLabel, totalCount } = props;
    const cx = viewBox?.cx ?? props.cx;
    const cy = viewBox?.cy ?? props.cy;

    if (!Number.isFinite(cx) || !Number.isFinite(cy)) {
        return null;
    }

    return (
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
            <tspan
                x={cx}
                y={cy - 5}
                className="font-bold"
                style={{
                    fontSize: '30px',
                    fontWeight: '700',
                    fill: 'var(--tw-prose-headings, #1f2937)'
                }}
            >
                {totalCount}
            </tspan>
            <tspan
                x={cx}
                y={cy + 20}
                className="uppercase tracking-wider font-semibold"
                style={{
                    fontSize: '12px',
                    fill: '#9ca3af'
                }}
            >
                {totalLabel}
            </tspan>
        </text>
    );
};

export function PieChartComponent({
    title,
    data,
    groupBy,
    width = "xl",
    onChartClick,
}: VolunteerPieChartProps) {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    // 1. SORT DATA BY DESCENDING COUNT
    // We use useMemo to prevent unnecessary re-sorting on every render
    const sortedData = useMemo(() => {
        return [...data].sort((a, b) => b.count - a.count);
    }, [data]);

    const totalVolunteers = sortedData.reduce((sum, item) => sum + item.count, 0);
    const widthClass = CHART_WIDTH_CLASSES[width];

    // Dynamic Height Calculation
    const dynamicHeight = 350 + (sortedData.length > 0 ? Math.ceil(sortedData.length / 2) * 40 : 0);

    const handleSliceClick = (slice: any) => {
        onChartClick?.({
            field: groupBy,
            value: slice.payload.name,
        });
    };

    const renderLegendText = (value: string, entry: any) => {
        const count = entry.payload?.count || 0;
        const percent = totalVolunteers > 0 ? (count / totalVolunteers) * 100 : 0;
        const isHovered = activeIndex === null || entry.payload?.index === activeIndex;

        return (
            <span className={`ms-2 text-sm transition-opacity duration-300 inline-flex items-center gap-2 ${isHovered ? 'opacity-100' : 'opacity-40'}`}>
                <span className="font-medium text-gray-700 dark:text-gray-200" title={value}>
                    {value}
                </span>
                <span className="text-gray-400 text-xs">
                    | {percent.toFixed(2)}% ({count})
                </span>
            </span>
        );
    };

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const percent = totalVolunteers > 0 ? (data.count / totalVolunteers) * 100 : 0;

            return (
                <div className="bg-white dark:bg-gray-800 p-3 shadow-xl rounded-lg border border-gray-100 dark:border-gray-700 z-50">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-1">{data.name}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: payload[0].fill }} />
                        <span>{data.count} Volunteers</span>
                        <span className="font-bold bg-gray-100 dark:bg-gray-700 px-1.5 rounded text-xs">
                            {Math.round(percent)}%
                        </span>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div
            className={`
                bg-white dark:bg-gray-600 rounded-xl shadow-sm border p-6
                dark:border-gray-500 mx-auto w-full ${widthClass}
                flex flex-col relative
            `}
            style={{ height: `${dynamicHeight}px`, minHeight: '400px' }}
        >
            {/* Header */}
            <div className="mb-2 flex items-center justify-between gap-4 shrink-0">
                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-50 tracking-tight">
                    {title}
                </h2>
            </div>

            <div className="mb-2 flex items-center justify-between gap-4 shrink-0">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-50 tracking-tight">
                    Total: {totalVolunteers}
                </h3>
            </div>

            <div className="border-b border-gray-200 dark:border-gray-600 mb-4 shrink-0" />

            {/* Chart Area */}
            <div className="flex-1 min-h-0 relative">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={sortedData} // Use sorted data here
                            dataKey="count"
                            nameKey="name"
                            cx="50%"
                            cy="40%"
                            innerRadius="40%"
                            outerRadius="65%"
                            paddingAngle={3}
                            cornerRadius={4}
                            cursor="pointer"
                            onClick={handleSliceClick}
                            onMouseEnter={(_, index) => setActiveIndex(index)}
                            onMouseLeave={() => setActiveIndex(null)}
                        >
                            {sortedData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                                    stroke="none"
                                    fillOpacity={activeIndex === null || activeIndex === index ? 1 : 0.3}
                                    className="transition-all duration-300 ease-in-out"
                                />
                            ))}

                            <Label
                                content={
                                    <CustomCenterLabel
                                        totalLabel="TOTAL"
                                        totalCount={totalVolunteers}
                                    />
                                }
                                position="center"
                            />
                        </Pie>

                        <Tooltip content={<CustomTooltip />} />

                        <Legend
                            layout="horizontal"
                            verticalAlign="bottom"
                            align="center"
                            iconType="circle"
                            iconSize={12}
                            wrapperStyle={{
                                paddingTop: "20px",
                                width: "100%"
                            }}
                            formatter={renderLegendText}
                            onMouseEnter={(entry) => {
                                // Find index in sorted data
                                const index = sortedData.findIndex(d => d.name === entry.value);
                                setActiveIndex(index);
                            }}
                            onMouseLeave={() => setActiveIndex(null)}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>

        </div>
    );
}